/**
 * BlockRenderer — Renders a list of CMS blocks as React components.
 *
 * Each block is { id, type, props }. Unknown types are skipped silently.
 * Used by both the public page renderer (/p/:slug) and the admin live preview.
 *
 * To add a new block type: create a render function below and add it to BLOCK_MAP.
 */
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ExternalLink } from "lucide-react";
import api, { resolveImageUrl, formatTRY } from "@/lib/api";
import HotelCard from "@/components/HotelCard";

// ---------- Helpers ----------
function youtubeIdFromUrl(url) {
    if (!url) return null;
    const m1 = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/);
    return m1 ? m1[1] : (url.length === 11 ? url : null);
}

function isInternal(url) {
    return url && (url.startsWith("/") || url.startsWith("#"));
}

// ---------- Block renderers ----------
function HeroBlock({ props }) {
    const bg = props.bg_image_url ? resolveImageUrl(props.bg_image_url) : null;
    const overlay = props.overlay_opacity ?? 0.5;
    const align = props.align || "center";
    const alignCls = align === "left" ? "items-start text-left" : align === "right" ? "items-end text-right" : "items-center text-center";
    return (
        <section data-testid="block-hero" className="relative overflow-hidden" style={{ minHeight: props.min_height || "480px" }}>
            {bg && <img src={bg} alt="" className="absolute inset-0 w-full h-full object-cover" />}
            <div className="absolute inset-0" style={{ background: `rgba(15,23,42,${overlay})` }} />
            <div className={`relative container mx-auto px-4 md:px-6 max-w-5xl py-20 md:py-32 flex flex-col justify-center ${alignCls}`}>
                {props.eyebrow && <span className="text-amber-300 font-semibold tracking-widest text-xs uppercase mb-3">{props.eyebrow}</span>}
                <h1 className="font-display text-4xl md:text-6xl font-bold text-white leading-[1.05] max-w-3xl">{props.title || "Başlık"}</h1>
                {props.subtitle && <p className="text-lg md:text-xl text-slate-200 mt-4 max-w-2xl">{props.subtitle}</p>}
                {(props.cta_label && props.cta_url) && (
                    isInternal(props.cta_url) ? (
                        <Link to={props.cta_url} className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-sky-500 hover:bg-sky-600 text-white font-bold text-sm shadow-xl shadow-sky-500/30 w-fit">{props.cta_label}</Link>
                    ) : (
                        <a href={props.cta_url} target="_blank" rel="noreferrer" className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-sky-500 hover:bg-sky-600 text-white font-bold text-sm shadow-xl shadow-sky-500/30 w-fit">{props.cta_label} <ExternalLink className="h-4 w-4" /></a>
                    )
                )}
            </div>
        </section>
    );
}

function TextBlock({ props }) {
    return (
        <section data-testid="block-text" className="container mx-auto px-4 md:px-6 max-w-3xl py-10">
            <div
                className="prose prose-slate dark:prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: props.content_html || "" }}
            />
        </section>
    );
}

function ImageBlock({ props }) {
    const inner = (
        <figure className={`relative ${props.rounded === false ? "" : "rounded-2xl overflow-hidden"}`}>
            <img src={resolveImageUrl(props.url)} alt={props.alt || ""} className="w-full h-auto object-cover" />
            {props.caption && <figcaption className="mt-2 text-center text-xs text-slate-500">{props.caption}</figcaption>}
        </figure>
    );
    const wrapped = props.link ? (
        isInternal(props.link) ? <Link to={props.link}>{inner}</Link> : <a href={props.link} target="_blank" rel="noreferrer">{inner}</a>
    ) : inner;
    if (props.full_width) {
        return <section data-testid="block-image" className="my-6">{wrapped}</section>;
    }
    return <section data-testid="block-image" className="container mx-auto px-4 md:px-6 max-w-5xl py-6">{wrapped}</section>;
}

function GalleryBlock({ props }) {
    const images = props.images || [];
    const cols = Math.max(2, Math.min(6, props.columns || 3));
    return (
        <section data-testid="block-gallery" className="container mx-auto px-4 md:px-6 max-w-7xl py-10">
            {props.title && <h2 className="font-display text-2xl md:text-3xl font-bold mb-6 text-slate-900 dark:text-white">{props.title}</h2>}
            <div className={`grid gap-3`} style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
                {images.map((img, i) => (
                    <a key={i} href={resolveImageUrl(img.url)} target="_blank" rel="noreferrer" className="relative aspect-square rounded-xl overflow-hidden group">
                        <img src={resolveImageUrl(img.url)} alt={img.alt || ""} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                    </a>
                ))}
            </div>
        </section>
    );
}

function CtaBlock({ props }) {
    const bg = props.bg_color || "linear-gradient(135deg, #0ea5e9, #0369a1)";
    return (
        <section data-testid="block-cta" className="py-10">
            <div className="container mx-auto px-4 md:px-6 max-w-5xl">
                <div className="rounded-3xl p-8 md:p-14 text-center" style={{ background: bg }}>
                    {props.eyebrow && <span className="text-white/70 text-xs font-bold uppercase tracking-widest">{props.eyebrow}</span>}
                    <h2 className="font-display text-3xl md:text-4xl font-bold text-white mt-2">{props.title || "Hadi Başlayalım"}</h2>
                    {props.subtitle && <p className="text-white/85 mt-3 max-w-xl mx-auto">{props.subtitle}</p>}
                    <div className="flex items-center justify-center gap-3 mt-6 flex-wrap">
                        {props.primary_label && props.primary_url && (
                            isInternal(props.primary_url) ? (
                                <Link to={props.primary_url} className="px-6 py-3 rounded-full bg-white text-sky-700 font-bold text-sm hover:bg-slate-100">{props.primary_label}</Link>
                            ) : (
                                <a href={props.primary_url} target="_blank" rel="noreferrer" className="px-6 py-3 rounded-full bg-white text-sky-700 font-bold text-sm hover:bg-slate-100">{props.primary_label}</a>
                            )
                        )}
                        {props.secondary_label && props.secondary_url && (
                            isInternal(props.secondary_url) ? (
                                <Link to={props.secondary_url} className="px-6 py-3 rounded-full bg-white/10 border border-white/30 text-white font-bold text-sm hover:bg-white/20">{props.secondary_label}</Link>
                            ) : (
                                <a href={props.secondary_url} target="_blank" rel="noreferrer" className="px-6 py-3 rounded-full bg-white/10 border border-white/30 text-white font-bold text-sm hover:bg-white/20">{props.secondary_label}</a>
                            )
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}

function FaqBlock({ props }) {
    const items = props.items || [];
    return (
        <section data-testid="block-faq" className="container mx-auto px-4 md:px-6 max-w-3xl py-12">
            {props.title && <h2 className="font-display text-2xl md:text-3xl font-bold text-center mb-8 text-slate-900 dark:text-white">{props.title}</h2>}
            <div className="space-y-3">
                {items.map((it, i) => <FaqItem key={i} q={it.question} a={it.answer} />)}
            </div>
        </section>
    );
}
function FaqItem({ q, a }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden">
            <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 dark:hover:bg-slate-800/40">
                <span className="font-semibold text-slate-900 dark:text-white">{q}</span>
                <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && <div className="px-4 pb-4 text-sm text-slate-600 dark:text-slate-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: a || "" }} />}
        </div>
    );
}

function EmbedVideoBlock({ props }) {
    const id = youtubeIdFromUrl(props.youtube_url || props.url);
    if (!id) return null;
    return (
        <section data-testid="block-video" className="container mx-auto px-4 md:px-6 max-w-4xl py-10">
            {props.title && <h2 className="font-display text-2xl md:text-3xl font-bold mb-4 text-slate-900 dark:text-white">{props.title}</h2>}
            <div className="aspect-video rounded-2xl overflow-hidden shadow-xl">
                <iframe className="w-full h-full" src={`https://www.youtube.com/embed/${id}`} title={props.title || "Video"} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            </div>
        </section>
    );
}

function CustomHtmlBlock({ props }) {
    return (
        <section data-testid="block-html" className="container mx-auto px-4 md:px-6 max-w-5xl py-6">
            <div dangerouslySetInnerHTML={{ __html: props.html || "" }} />
        </section>
    );
}

function HotelListBlock({ props }) {
    const [hotels, setHotels] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        const limit = props.limit || 6;
        const source = props.source || "featured";
        let endpoint = "/hotels/featured?limit=" + limit;
        if (source === "deals") endpoint = "/hotels/deals?limit=" + limit;
        else if (source === "destination" && props.destination_slug) endpoint = `/hotels?destination=${encodeURIComponent(props.destination_slug)}&limit=${limit}`;
        api.get(endpoint).then((r) => setHotels(Array.isArray(r.data) ? r.data : (r.data.items || []))).finally(() => setLoading(false));
    }, [props.source, props.destination_slug, props.limit]);
    const cols = Math.max(2, Math.min(4, props.columns || 3));
    return (
        <section data-testid="block-hotel-list" className="container mx-auto px-4 md:px-6 max-w-7xl py-10">
            {props.title && <h2 className="font-display text-2xl md:text-3xl font-bold mb-6 text-slate-900 dark:text-white">{props.title}</h2>}
            {loading ? <div className="text-slate-500">Yükleniyor...</div> : (
                <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
                    {hotels.slice(0, props.limit || 6).map((h) => <HotelCard key={h.id} hotel={h} />)}
                </div>
            )}
        </section>
    );
}

function SpacerBlock({ props }) {
    const h = props.height || "60px";
    return <div data-testid="block-spacer" style={{ height: h }} />;
}

// ---------- Block map ----------
const BLOCK_MAP = {
    hero: HeroBlock,
    text: TextBlock,
    image: ImageBlock,
    gallery: GalleryBlock,
    cta: CtaBlock,
    faq: FaqBlock,
    embed_video: EmbedVideoBlock,
    custom_html: CustomHtmlBlock,
    hotel_list: HotelListBlock,
    spacer: SpacerBlock,
};

export default function BlockRenderer({ blocks = [] }) {
    return (
        <div data-testid="block-renderer">
            {blocks.map((b) => {
                const Comp = BLOCK_MAP[b.type];
                if (!Comp) return null;
                return <Comp key={b.id} props={b.props || {}} />;
            })}
        </div>
    );
}

export { BLOCK_MAP };
